import React from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import type { SafetyScanHandoffParams } from '../../domain/safetyCases/safetyScanWorkspaceHandoff';
import {
  formatAfterSignedScanTimestamp,
  formatSignedScanScopeLabel,
} from '../../domain/incidents/afterSignedScanReview';
import { safetyCaseCardTitle } from '../../domain/safetyCases/safetyCaseWorklist';

export const UPDATED_SAFETY_SCAN_SCOPE_IMMUTABILITY_COPY =
  'The previous Signed Safety Scan remains frozen. This scope defines the new activity window for the Updated Safety Scan.';

type ScopeField = {
  label: string;
  value: string;
};

type Props = {
  handoff: SafetyScanHandoffParams;
  linkedCase: SafetyCaseRecord | null;
  updatedScopeStartLabel: string;
  updatedScopeEndLabel: string;
  zoneLabel: string;
  cameraLabel: string;
  riskPatternLabel: string;
  observationCount: number | null;
  observationCountLoading?: boolean;
  onConfirm: () => void;
  onAdjustScope: () => void;
  confirmDisabled?: boolean;
};

function ScopeFieldRow({ label, value }: ScopeField) {
  return (
    <Typography variant="body2">
      <Box component="span" sx={{ fontWeight: 700 }}>{`${label}: `}</Box>
      {value}
    </Typography>
  );
}

export default function UpdatedSafetyScanScopeConfirmationPanel({
  handoff,
  linkedCase,
  updatedScopeStartLabel,
  updatedScopeEndLabel,
  zoneLabel,
  cameraLabel,
  riskPatternLabel,
  observationCount,
  observationCountLoading = false,
  onConfirm,
  onAdjustScope,
  confirmDisabled = false,
}: Props) {
  const linkedSafetyCaseLabel = linkedCase
    ? safetyCaseCardTitle(linkedCase)
    : String(handoff.safetyCaseId || '').trim() || 'Not linked yet';
  const latestSignedScanLabel = String(handoff.latestSignedScanId || '').trim() || 'Not recorded';
  const signedTimestampLabel = formatAfterSignedScanTimestamp(
    handoff.signedScanAt || handoff.newActivityWindowStart,
  );
  const previousSignedScopeLabel = formatSignedScanScopeLabel(
    handoff.signedScanScopeStart,
    handoff.signedScanScopeEnd,
  );
  const observationCountLabel = observationCountLoading
    ? 'Loading…'
    : observationCount == null
      ? 'Not available'
      : `${observationCount.toLocaleString()} observations in selected updated scan scope`;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 2.5 }}
      data-testid="updated-safety-scan-scope-confirmation"
    >
      <Stack spacing={1.5}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Confirm Updated Safety Scan Scope
        </Typography>
        <Alert severity="info" sx={{ py: 0.5 }}>
          {UPDATED_SAFETY_SCAN_SCOPE_IMMUTABILITY_COPY}
        </Alert>
        <Box
          sx={{
            display: 'grid',
            gap: 0.75,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          <ScopeFieldRow label="Linked Safety Case" value={linkedSafetyCaseLabel} />
          <ScopeFieldRow label="Latest Signed Safety Scan" value={latestSignedScanLabel} />
          <ScopeFieldRow label="Latest Signed Safety Scan timestamp" value={signedTimestampLabel} />
          <ScopeFieldRow label="Previous signed scan scope" value={previousSignedScopeLabel} />
          <ScopeFieldRow label="Updated scan scope start" value={updatedScopeStartLabel} />
          <ScopeFieldRow label="Updated scan scope end" value={updatedScopeEndLabel} />
          <ScopeFieldRow label="Zone" value={zoneLabel} />
          <ScopeFieldRow label="Camera" value={cameraLabel} />
          <ScopeFieldRow label="Risk pattern / policy" value={riskPatternLabel} />
          <ScopeFieldRow label="Observation count in selected scope" value={observationCountLabel} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            Confirm Updated Safety Scan Scope
          </Button>
          <Button variant="outlined" onClick={onAdjustScope}>
            Adjust Scope
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
